import {
  useContactEntitiesSuspense,
  useContactWithSuspense,
  type ContactsRequest,
  type ContactsResponse
} from "@swivu/client-api";
import { createContext, Suspense, useContext } from "react";
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import {
  Badge,
  cn,
  decodeFA,
  DrawerOrDialog,
  ErrorBoundary,
  LOUD_CHIP,
  META_LABEL,
  RichText,
  tagTone
} from "@swivu/shared-ui";
import SuspenseWithDelay from "@swivu/shared-ui/src/components/SuspenseWithDelay.js";

const GUMMY_RAISED_CARD = cn(
  "rounded-surface-xl bg-white dark:bg-mono-900",
  "shadow-card-deep"
);

const GUMMY_CONTACT_PILL = cn(
  "inline-flex h-16 w-16 items-center justify-center rounded-full text-2xl",
  "shadow-raised text-shadow-on-color-strong",
  "bg-tile-people text-white",
  "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96]",
  "motion-reduce:transform-none motion-reduce:transition-none",
  "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
);

type ViewContactVariables = ContactsRequest & {
  baseUrl: {
    resources?: string;
    tasks?: string;
    users?: string;
    clients?: string;
  };
};
type ViewContactContextProps = ViewContactVariables & {
  contact: ContactsResponse;
};

const ContactContext = createContext<ViewContactContextProps | null>(null);

const ContactProvider = ({
  children,
  ...props
}: ViewContactVariables & {
  children: React.ReactNode;
}) => {
  const { data: contact } = useContactWithSuspense({
    variables: {
      ...props
    }
  });
  return (
    <ContactContext.Provider value={{ ...props, contact }}>
      {children}
    </ContactContext.Provider>
  );
};

const useContact = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error("useContact must be used within a ContactProvider");
  }
  return context;
};

const Contact = () => {
  const { contact, id } = useContact();
  return (
    <div className="max-w-full">
      <div className={cn("overflow-hidden mt-4", GUMMY_RAISED_CARD)}>
        <div
          className="tile-color-strip h-2.5"
          style={{ backgroundColor: "#f97316" }}
        />

        <div className="mobile-pref-card p-6">
          <div className="mobile-pref-contact-header flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2
                className="mobile-pref-section-title text-4xl leading-tight font-black tracking-tight break-words text-mono-900 dark:text-white"
                style={{ viewTransitionName: `contact-name-${id}` }}
              >
                {contact.name || "Untitled Contact"}
              </h2>
              {contact.address && (
                <div className="flex items-start gap-2 mt-2">
                  <svg
                    className="size-6 mt-0.5 flex-shrink-0 text-mono-500 dark:text-mono-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span className="mobile-pref-card-body text-base font-black text-mono-700 dark:text-mono-200">
                    {contact.address}
                  </span>
                </div>
              )}
            </div>

            <div className="mobile-pref-contact-actions flex items-center gap-3">
              {contact?.phone && (
                <button
                  type="button"
                  className={cn(
                    "mobile-pref-control-tight",
                    GUMMY_CONTACT_PILL
                  )}
                  aria-label={`Call ${contact.name}`}
                  onClick={() => {
                    window.location.href = `tel:${contact.phone}`;
                  }}
                >
                  <i
                    className={`mobile-pref-detail-inline-icon fa-regular ${decodeFA("phone")} fa-fw`}
                  />
                </button>
              )}
              {contact?.email && (
                <button
                  type="button"
                  className={cn(
                    "mobile-pref-control-tight",
                    GUMMY_CONTACT_PILL
                  )}
                  aria-label={`Email ${contact.name}`}
                  onClick={() => {
                    window.location.href = `mailto:${contact.email}`;
                  }}
                >
                  <i
                    className={`mobile-pref-detail-inline-icon fa-regular ${decodeFA("envelope")} fa-fw`}
                  />
                </button>
              )}
            </div>
          </div>

          {contact.notes_text && (
            <div className="py-4 mb-2">
              <h4 className={cn("mobile-pref-card-meta mb-2", META_LABEL)}>
                Notes
              </h4>
              <RichText
                className="mobile-pref-rich-text font-body text-base font-bold text-mono-800 dark:text-mono-200"
                html={contact.notes_text}
              />
            </div>
          )}

          <div className="flex flex-wrap py-2 text-sm gap-x-6 gap-y-4">
            {contact.category && (
              <div className="flex flex-col gap-1">
                <span className={cn("mobile-pref-card-meta", META_LABEL)}>
                  Category
                </span>
                <span
                  className={cn(
                    LOUD_CHIP,
                    "self-start",
                    tagTone(contact.category)
                  )}
                >
                  {contact.category}
                </span>
              </div>
            )}
            {contact.status && (
              <div className="flex flex-col gap-1">
                <span className={cn("mobile-pref-card-meta", META_LABEL)}>
                  Status
                </span>
                <span
                  className={cn(LOUD_CHIP, "self-start", "bg-tile-primary")}
                >
                  {contact.status}
                </span>
              </div>
            )}
          </div>

          {contact.tag && contact.tag.length > 0 && (
            <div className="my-1 py-2 mt-2 pt-3">
              <h4 className={cn("mobile-pref-card-meta mb-2", META_LABEL)}>
                Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {contact.tag
                  .filter((t): t is string => !!t)
                  .map((tag, index) => (
                    <span key={index} className={cn(LOUD_CHIP, tagTone(tag))}>
                      {tag}
                    </span>
                  ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4">
            <h4 className={cn("mobile-pref-card-meta mb-3", META_LABEL)}>
              Linked Entities
            </h4>
            <Suspense fallback={<ContactPageSkeleton section="linked" />}>
              <ErrorBoundary>
                <ContactLinkedEntities />
              </ErrorBoundary>
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactLinkedEntities = () => {
  const { contact, ...variables } = useContact();
  const contactIdNum = parseInt(variables.id, 10);

  const { data: resources } = useContactEntitiesSuspense("resources")({
    variables: {
      ...variables,
      id: contactIdNum,
      entity_type: "resources"
    }
  });
  const { data: tasks } = useContactEntitiesSuspense("tasks")({
    variables: {
      ...variables,
      id: contactIdNum,
      entity_type: "tasks"
    }
  });
  const { data: users } = useContactEntitiesSuspense("users")({
    variables: {
      ...variables,
      id: contactIdNum,
      entity_type: "users"
    }
  });
  const { data: clients } = useContactEntitiesSuspense("clients")({
    variables: {
      ...variables,
      id: contactIdNum,
      entity_type: "clients"
    }
  });

  const resourceItems = resources.map((resource) => ({
    id: resource.id,
    name: resource.name,
    link: variables.baseUrl.resources
      ? `${variables.baseUrl.resources}${resource.id}`
      : undefined
  }));
  const taskItems = tasks.map((task) => ({
    id: task.id,
    name: task.name,
    link: variables.baseUrl.tasks
      ? `${variables.baseUrl.tasks}${task.id}`
      : undefined
  }));
  const userItems = users.map((user) => ({
    id: user.id,
    name: user.name,
    link: variables.baseUrl.users
      ? `${variables.baseUrl.users}${user.id}`
      : undefined
  }));
  const clientItems = clients.map((client) => ({
    id: client.id,
    name: client.name,
    link: variables.baseUrl.clients
      ? `${variables.baseUrl.clients}${client.id}`
      : undefined
  }));

  const allLinkedItems = [
    {
      title: "Resources",
      items: resourceItems,
      Icon: WrenchScrewdriverIcon,
      tile: "bg-tile-calendar"
    },
    {
      title: "Tasks",
      items: taskItems,
      Icon: ClipboardDocumentListIcon,
      tile: "bg-tile-success"
    },
    {
      title: "Users",
      items: userItems,
      Icon: UserGroupIcon,
      tile: "bg-tile-people"
    },
    {
      title: "Clients",
      items: clientItems,
      Icon: BuildingOfficeIcon,
      tile: "bg-tile-achievement"
    }
  ].filter((group) => group.items.length > 0);

  if (allLinkedItems.length === 0) {
    return (
      <p className="text-lg font-black text-mono-700 dark:text-mono-200">
        No linked entities.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-12">
      {allLinkedItems.map((group) => (
        <ContactLinkedUI
          key={group.title}
          title={group.title}
          name={contact.name}
          items={group.items}
          Icon={group.Icon}
          tile={group.tile}
        />
      ))}
    </div>
  );
};

type ContactLinkedUIProps = {
  title: string;
  name?: string | null;
  items: {
    id: number;
    name: string;
    link?: string;
  }[];
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  tile: string;
};

const ContactLinkedUI = ({
  title,
  name,
  items,
  Icon,
  tile
}: ContactLinkedUIProps) => {
  if (items.length === 0) return null;
  return (
    <DrawerOrDialog.Root drawer={{}} dialog={{}}>
      <div className="relative">
        <DrawerOrDialog.Trigger asChild>
          <button
            type="button"
            className={cn(
              "shadow-raised text-shadow-on-color-strong",
              "relative flex aspect-square w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-surface-tile px-4 py-5 text-white",
              tile,
              "transition-transform duration-150",
              "hover:-translate-y-0.5 active:scale-[0.96]",
              "motion-reduce:transform-none motion-reduce:transition-none",
              "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
            )}
          >
            <span aria-hidden className="top-gloss-large" />
            <Icon className="relative size-16 flex-none" aria-hidden />
            <span className="relative text-center text-2xl font-black leading-[1.15] tracking-tight">
              {title}
            </span>
          </button>
        </DrawerOrDialog.Trigger>
        {items.length > 0 && (
          <Badge
            count={items.length}
            className="left-auto -top-3 -right-3 h-12 min-w-12 px-3 text-lg font-black pointer-events-none bg-white text-mono-900 shadow-raised border-0 dark:bg-mono-900 dark:text-white"
          />
        )}
      </div>
      <DrawerOrDialog.Content className="px-4 pb-6 dark:text-mono-200 bg-white dark:bg-mono-950">
        <DrawerOrDialog.Title className="mobile-pref-section-title text-left pb-3 pt-4 text-3xl font-black tracking-tight leading-tight text-mono-900 dark:text-white">
          {title}
          {name ? ` - ${name}` : ``}
        </DrawerOrDialog.Title>
        <ul className="flex flex-col gap-3 min-w-0 mt-4">
          {items.map((item) => (
            <li key={item.id}>
              {item.link ? (
                <Link
                  to={`${item.link}`}
                  className={cn(
                    "mobile-pref-list-button w-full text-left px-6 flex items-center justify-between gap-4 min-h-16",
                    "shadow-card rounded-surface-md py-5 bg-white dark:bg-mono-900 text-mono-900 dark:text-white",
                    "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]",
                    "motion-reduce:transform-none motion-reduce:transition-none",
                    "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
                  )}
                >
                  <span className="mobile-pref-card-body text-lg font-black">
                    {item.name}
                  </span>
                  <ArrowRightIcon
                    className="size-7 flex-none text-mono-700 dark:text-mono-200"
                    strokeWidth={3}
                  />
                </Link>
              ) : (
                <span className="mobile-pref-card-body text-lg font-black block py-4 px-4">
                  {item.name}
                </span>
              )}
            </li>
          ))}
        </ul>
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

const ContactPageSkeleton = ({ section }: { section?: "main" | "linked" }) => {
  if (section === "linked") {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2 animate-pulse">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="bg-mono-100 dark:bg-mono-800 p-3 rounded-surface-lg space-y-2"
          >
            <div className="h-4 bg-mono-200 dark:bg-mono-700 rounded-surface-xs w-3/4"></div>
            <div className="h-3 bg-mono-200 dark:bg-mono-700 rounded-surface-2xs w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="max-w-full animate-pulse">
      <div className="overflow-hidden rounded-surface-xl bg-white dark:bg-mono-900 shadow-card-deep mt-4">
        <div className="h-2.5 bg-mono-200 dark:bg-mono-700"></div>
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-start">
            <div className="space-y-1.5">
              <div className="h-7 w-48 bg-mono-200 dark:bg-mono-700 rounded-surface-xs"></div>
              <div className="h-4 w-32 bg-mono-200 dark:bg-mono-700 rounded-surface-2xs"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-12 w-12 bg-mono-200 dark:bg-mono-700 rounded-full"></div>
              <div className="h-12 w-12 bg-mono-200 dark:bg-mono-700 rounded-full"></div>
            </div>
          </div>
          <div className="h-16 bg-mono-200 dark:bg-mono-700 rounded-surface-xs"></div>
          <div className="flex gap-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <div className="h-3 w-16 bg-mono-200 dark:bg-mono-700 rounded-surface-2xs"></div>
                <div className="h-11 w-24 bg-mono-200 dark:bg-mono-700 rounded-full"></div>
              </div>
            ))}
          </div>
          <div className="h-10 bg-mono-200 dark:bg-mono-700 rounded-full"></div>
          <div className="h-4 w-24 bg-mono-200 dark:bg-mono-700 rounded-surface-2xs mt-4"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="bg-mono-100 dark:bg-mono-800 p-3 rounded-surface-lg space-y-2"
              >
                <div className="h-4 bg-mono-200 dark:bg-mono-700 rounded-surface-xs w-3/4"></div>
                <div className="h-3 bg-mono-200 dark:bg-mono-700 rounded-surface-2xs w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ContactProviderWrapper = ({
  children,
  ...props
}: ViewContactVariables & {
  children: React.ReactNode;
}) => {
  return (
    <SuspenseWithDelay fallback={<ContactPageSkeleton />}>
      <ErrorBoundary>
        <ContactProvider {...props}>{children}</ContactProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

Contact.Provider = ContactProviderWrapper;
Contact.Skeleton = ContactPageSkeleton;
Contact.useContact = useContact;
export default Contact;
